import React, { useState, useEffect } from 'react';
import '../css/EditableWidget.css';

const EditableWidget = ({ 
  title, 
  items, 
  onSave, 
  dateFormat = true, 
  language
}) => {
  const [editing, setEditing] = useState(false);
  const [editedItems, setEditedItems] = useState(items);
  const [showModal, setShowModal] = useState(false);

  // Update local items when parent items change
  useEffect(() => {
    setEditedItems(items);
  }, [items]);

  const handleEditClick = () => {
    setEditing(true);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditing(false);
    // Reset to original items
    setEditedItems(items);
  };

  const handleSaveChanges = () => {
    onSave(editedItems);
    setShowModal(false);
    setEditing(false);
  };

  const handleAddItem = () => {
    const newItem = {
      id: Date.now(),
      date: '',
      content: '',
      contentEn: ''
    };
    setEditedItems([...editedItems, newItem]);
  };

  const handleRemoveItem = (id) => {
    setEditedItems(editedItems.filter(item => item.id !== id));
  };

  const handleItemChange = (id, field, value) => {
    setEditedItems(editedItems.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  // Display items according to current language
  const displayItems = editedItems.map(item => (
    <div key={item.id} className={dateFormat ? "item-with-date" : "item"}>
      {dateFormat && (
        <span className="item-date">{item.date}</span>
      )}
      <span className="item-content">
        {language === 'en' && item.contentEn ? item.contentEn : item.content}
      </span>
    </div>
  ));

  return (
    <div className="editable-widget">
      <h3>{title}</h3>
      
      <div className="widget-content">
        {displayItems}
      </div>
      
      {/* Edit button */}
      <button className="edit-button" onClick={handleEditClick}>
  <span className="icon">✏️</span>
</button>
      
      {/* Modal for editing */}
      {showModal && (
        <div className="edit-modal-overlay">
          <div className="edit-modal">
            <div className="edit-modal-header">
              <h3>Edit {title}</h3>
              <button className="close-modal-button" onClick={handleCloseModal}>&times;</button>
            </div>
            
            <div className="edit-modal-content">
              <div className="edit-instructions">
                Fill out both Dutch and English content for bilingual support.
              </div>
              
              {editedItems.map((item, index) => (
                <div key={item.id} className="edit-item">
                  <div className="edit-item-header">
                    <span>Item {index + 1}</span>
                    <button 
                      className="remove-item-button" 
                      onClick={() => handleRemoveItem(item.id)}
                    >
                      Remove
                    </button>
                  </div>
                  
                  {dateFormat && (
                    <div className="edit-field">
                      <label>Date:</label>
                      <input 
                        type="text" 
                        value={item.date} 
                        onChange={(e) => handleItemChange(item.id, 'date', e.target.value)}
                        placeholder="e.g., 15 mei 2025"
                      />
                    </div>
                  )}
                  
                  <div className="edit-field">
                    <label>Dutch Content:</label>
                    <textarea
                      value={item.content}
                      onChange={(e) => handleItemChange(item.id, 'content', e.target.value)}
                      placeholder="Enter content in Dutch"
                    />
                  </div>
                  
                  <div className="edit-field">
                    <label>English Content:</label>
                    <textarea
                      value={item.contentEn || ''}
                      onChange={(e) => handleItemChange(item.id, 'contentEn', e.target.value)}
                      placeholder="Enter content in English"
                    />
                  </div>
                </div>
              ))}
              
              <button className="add-item-button" onClick={handleAddItem}>
                + Add New Item
              </button>
            </div>
            
            <div className="edit-modal-footer">
              <button className="cancel-button" onClick={handleCloseModal}>Cancel</button>
              <button className="save-button" onClick={handleSaveChanges}>Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditableWidget;